import { motion } from "framer-motion";
import { Handshake, Truck, Smile, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [Handshake, Truck, Smile, MapPin];

export const Features = () => {
  const { t } = useLanguage();
  if (!t.features) return null;

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0055FF]">
            {t.features.kicker}
          </div>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold tracking-tight text-neutral-900">
            {t.features.title}
          </h2>
          {t.features.subtitle && (
            <p
              data-testid="features-subtitle"
              className="mt-4 text-lg text-neutral-600 leading-relaxed"
            >
              {t.features.subtitle}
            </p>
          )}
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.features.items.map((item, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div
                key={item.t}
                data-testid={`features-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative rounded-3xl bg-[#F8FAFC] border border-slate-200/70 p-8 hover:-translate-y-1.5 transition-all duration-300 hover:shadow-xl hover:shadow-[#0055FF]/10"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center mb-6 group-hover:bg-[#0055FF] group-hover:text-white transition-colors">
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-neutral-900">
                  {item.t}
                </h3>
                <p className="mt-3 text-neutral-600 leading-relaxed">{item.d}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-12 md:mt-16 max-w-xl mx-auto"
          data-testid="features-image-wrapper"
        >
          <img
            src="/images/fp-events-3.webp"
            alt={t.features.imgAlt}
            loading="lazy"
            decoding="async"
            width={1400}
            height={787}
            data-testid="features-image"
            className="w-full h-auto rounded-3xl border border-slate-200 shadow-xl shadow-neutral-900/5 object-cover"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
